import { Select, Space, Table } from "antd";
import { AnyObject } from "antd/es/_util/type";
import { ColumnsType, TableProps } from "antd/es/table";
import { ReactNode } from "react";
import SelectDownIcon from "../../assets/icons/SelectDownIcon";
import { addZeroIfInRange } from "../../utils/number";
import { selectSearchOption, selectStringOption } from "../../utils/utils";
import CustomPagination from "./CustomPagination";

interface CustomTableProps<T> extends TableProps<any> {
  tableTitle?: string;
  tableTitleButtonName?: string;
  page?: number;
  limit?: number;
  total?: number;
  columns: ColumnsType<T>;
  filterSection?: ReactNode;
  hideRowSelect?: boolean;
  hidePagination?: boolean;
  hideDifferentRowColor?: boolean;
  rootClassName?: string;
  handlePagination?: (paginationPage: number, paginationLimit: number) => void;
  handleLimit?: (val: number) => void;
  handleTitleButton?: any;
}

const CustomTable = <T extends AnyObject>({
  tableTitle,
  tableTitleButtonName,
  loading,
  page,
  limit,
  total,
  dataSource,
  columns,
  filterSection,
  hideRowSelect,
  hidePagination,
  hideDifferentRowColor,
  className = "",
  rootClassName = "",
  handleLimit,
  handlePagination,
  handleTitleButton,
  ...rest
}: CustomTableProps<T>) => {
  const isShowTopSection = (!hideRowSelect && total) || filterSection;
  const isShowPagination =
    !hidePagination && dataSource?.length && total && limit;

  return (
    <div
      className={`border border-light rounded bg-white small-input-section ${rootClassName}`}
    >
      {isShowTopSection && (
        <>
          <div
            className={`flex justify-between items-center p-4 pb-0 ${
              tableTitle || tableTitleButtonName ? "mb-5" : "mb-0"
            }`}
          >
            <div className=" font-semibold text-[18px] text-[#343A40]">
              {tableTitle}
            </div>
            <div
              onClick={handleTitleButton}
              className="justify-start text-blue-500 text-base font-medium font-['Inter'] leading-normal cursor-pointer"
            >
              {tableTitleButtonName}
            </div>
          </div>
          <Space
            wrap
            className="p-4 justify-between w-full border-b border-b-light"
          >
            <div>
              {!hideRowSelect && (
                <div className="flex items-center gap-2.5 text-body text-sm font-semibold">
                  <span>Total Rows</span>
                  <Select
                    size="small"
                    showSearch
                    value={limit}
                    onChange={handleLimit}
                    filterOption={selectSearchOption}
                    options={selectStringOption(
                      ["10", "20", "50", "100", "1000"],
                      "number"
                    )}
                    className="min-w-[70px]"
                    suffixIcon={<SelectDownIcon />}
                  />
                  <span>of {addZeroIfInRange(total || 0)}</span>
                </div>
              )}
            </div>

            {filterSection ? (
              <Space wrap size={10}>
                {filterSection}
              </Space>
            ) : null}
          </Space>
        </>
      )}

      {/* Ant Design Table */}
      <Table
        loading={loading}
        columns={columns}
        pagination={false}
        dataSource={dataSource}
        className={`custom-table custom-scrollbar ${className} ${
          !hideDifferentRowColor && dataSource?.length
            ? "different-row-color"
            : ""
        } ${!isShowTopSection ? "table-rounded-top" : ""} ${
          !isShowPagination ? "table-hide-border-bottom" : ""
        }`}
        {...rest}
      />

      {isShowPagination ? (
        <CustomPagination
          total={total}
          limit={limit}
          page={page}
          hidePagination={hidePagination}
          handlePagination={handlePagination}
        />
      ) : null}
    </div>
  );
};

export default CustomTable;
